// The interior-paper preselect is COLOR-AWARE rather than a single static
// default: black & white seeds 50# offset (our economy text stock), full color
// seeds 60# offset (heavier, with less show-through). 'mixed' (part color, part
// B&W body) seeds 60# too — the heavier stock holds the color pages without
// show-through on the text pages. All three stocks are in the seed's approved
// picker list. The values are the `interior_paper` option KEYS Nexus's
// ServiceAttributeOption mirror carries, never labels.
export const COLOR_PAPER_DEFAULT: Record<string, string> = {
  black_and_white: '50_offset',
  color: '60_offset',
  mixed: '60_offset',
};

export type ColorPaperPlan = {
  // The stock to write into interior_paper, or null to leave the value alone.
  write: string | null;
  // The stock the preselect owns after this pass: the one it last wrote, while
  // the line still holds it. Null once any other value stands.
  claim: string | null;
};

// One pass of the color-driven paper preselect, as a pure decision so the
// effect in SchemaForm holds no rule of its own. It writes only while paper is
// empty or still holds the stock this preselect last wrote, so a customer pick
// or a saved quote's value is never overwritten.
//
// The claim is released the moment any other value stands. Held past that, it
// outlives the stock it was taken on: a line seeded 50# on black & white, moved
// to 60# by the customer and then to full color kept claiming 50#, so the next
// time the customer picked 50# it read as the preselect's own and snapped back
// to 60# — silently, on every line configured in that order.
export function planColorPaper(args: {
  current: unknown;
  // The color the line is printed in ('color' | 'black_and_white' | 'mixed'),
  // undefined while it is not chosen yet.
  color: unknown;
  claim: string | null;
}): ColorPaperPlan {
  const { current } = args;
  const isEmpty = current === undefined || current === null || current === '';
  const claim = !isEmpty && current !== args.claim ? null : args.claim;
  const desired = typeof args.color === 'string' ? COLOR_PAPER_DEFAULT[args.color] : undefined;
  if (!desired || current === desired) return { write: null, claim };
  if (isEmpty || current === claim) return { write: desired, claim: desired };
  return { write: null, claim };
}
